<!-- ========== 档口退货管理页面 ========== -->
<template>
  <div class="return-management">
    <div class="page-header">
      <h1>{{ $t('stall.return') }}</h1>
      <p class="subtitle">{{ $t("return.subtitle") }}</p>
    </div>
    
    <!-- 搜索和筛选 -->
    <div class="search-section">
      <div class="search-form">
        <div class="form-group">
          <label>{{ $t("return.dateRange") }}</label>
          <input v-model="filters.startDate" type="date" class="form-control" />
        </div>
        <div class="form-group">
          <label>{{ $t("return.to") }}</label>
          <input v-model="filters.endDate" type="date" class="form-control" />
        </div>
        <div class="form-group">
          <label>{{ $t('navigation.products') }}</label>
          <select v-model="filters.productId" class="form-control">
            <option value="">{{ $t('common.all') }}{{ $t('navigation.products') }}</option>
            <option v-for="product in products" :key="product.id" :value="product.id">
              {{ product.name }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label>{{ $t("return.returnType") }}</label>
          <select v-model="filters.returnType" class="form-control">
            <option value="">{{ $t('common.all') }}类型</option>
            <option value="customer">{{ $t("return.customerReturn") }}</option>
            <option value="supplier">{{ $t("return.supplierReturn") }}</option>
            <option value="damage">{{ $t("return.damageReturn") }}</option>
          </select>
        </div>
        <button class="btn btn-primary" @click="searchReturns">
          <i class="icon-search"></i> {{ $t("return.search") }}
        </button>
        <button class="btn btn-secondary" @click="resetFilters">{{ $t('common.reset') }}</button>
      </div>
      
      <div class="actions">
        <button class="btn btn-primary" @click="showNewReturnModal">
          <i class="icon-plus"></i> {{ $t("return.newReturn") }}
        </button>
        <button class="btn btn-secondary" @click="exportReturns">
          <i class="icon-download"></i> {{ $t("return.export") }}
        </button>
      </div>
    </div>
    
    <!-- 退货统计 -->
    <div class="statistics-bar">
      <div class="stat-item">
        <span class="label">{{ $t("return.totalAmount") }}：</span>
        <span class="value">¥{{ totalReturnAmount }}</span>
      </div>
      <div class="stat-item">
        <span class="label">{{ $t("return.totalCount") }}：</span>
        <span class="value">{{ totalReturnCount }} {{ $t("return.records") }}</span>
      </div>
      <div class="stat-item">
        <span class="label">{{ $t("return.avgPrice") }}：</span>
        <span class="value">¥{{ averagePrice }}</span>
      </div>
      <div class="stat-item">
        <span class="label">总{{ $t('navigation.products') }}数：</span>
        <span class="value">{{ totalItems }} {{ $t("return.items") }}</span>
      </div>
    </div>
    
    <!-- 退货列表 -->
    <div class="table-container">
      <table class="return-table">
        <thead>
          <tr>
            <th width="60">{{ $t("return.seq") }}</th>
            <th width="120">{{ $t("return.date") }}</th>
            <th width="100">{{ $t('navigation.products') }}名称</th>
            <th width="80">{{ $t('navigation.products') }}编码</th>
            <th width="80">退货{{ $t('common.quantity') }}</th>
            <th width="80">{{ $t("return.unitPrice") }}</th>
            <th width="100">退货{{ $t('common.amount') }}</th>
            <th width="100">{{ $t("return.returnType") }}</th>
            <th width="100">处理{{ $t('common.status') }}</th>
            <th width="150">{{ $t('common.action') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="returnList.length === 0">
            <td colspan="10" class="empty-cell">
              <div class="empty-state">
                <i class="icon-inbox"></i>
                <p>{{ $t("return.noRecords") }}</p>
              </div>
            </td>
          </tr>
          <tr v-for="(returnItem, index) in returnList" :key="returnItem.id">
            <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
            <td>{{ formatDate(returnItem.createdAt) }}</td>
            <td>
              <div class="product-cell">
                <div class="product-name">{{ returnItem.productName }}</div>
              </div>
            </td>
            <td>{{ returnItem.productCode }}</td>
            <td>{{ returnItem.quantity }}</td>
            <td>¥{{ returnItem.unitPrice }}</td>
            <td class="amount">¥{{ returnItem.totalAmount }}</td>
            <td>{{ returnTypeLabel[returnItem.returnType] }}</td>
            <td>
              <span :class="['status', `status-${returnItem.status}`]">
                {{ statusLabel[returnItem.status] }}
              </span>
            </td>
            <td>
              <button class="btn-action" @click="viewReturnDetail(returnItem.id)">
                <i class="icon-eye"></i> {{ $t("return.view") }}
              </button>
              <button class="btn-action" @click="editReturn(returnItem.id)">
                <i class="icon-edit"></i> {{ $t('common.edit') }}
              </button>
              <button class="btn-action danger" @click="deleteReturn(returnItem.id)">
                <i class="icon-trash"></i> {{ $t('common.delete') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <!-- 分页 -->
      <div class="pagination-container">
        <div class="pagination-info">
          {{ totalCount }} {{ $t("pagination.records") }}，
          <select v-model.number="pageSize" @change="searchReturns" class="page-size-select">
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
        <div class="pagination-controls">
          <button :disabled="currentPage === 1" @click="currentPage--" class="btn-page">
            <i class="icon-chevron-left"></i> {{ $t("pagination.prev") }}
          </button>
          <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
          <button :disabled="currentPage === totalPages" @click="currentPage++" class="btn-page">
            {{ $t("pagination.next") }} <i class="icon-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
    
    <!-- 新增退货单模态框 -->
    <div v-if="showReturnModal" class="modal-overlay" @click="closeReturnModal">
      <div class="modal-dialog" @click.stop>
        <div class="modal-header">
          <h2>{{ returnFormTitle }}</h2>
          <button class="btn-close" @click="closeReturnModal">×</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitReturn">
            <div class="form-group">
              <label>{{ $t('navigation.products') }} <span class="required">*</span></label>
              <select v-model="returnForm.productId" class="form-control" required>
                <option value="">{{ $t('common.select') }}{{ $t('navigation.products') }}</option>
                <option v-for="product in products" :key="product.id" :value="product.id">
                  {{ product.name }} - 当前库存: {{ product.stock }}
                </option>
              </select>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>退货{{ $t('common.quantity') }} <span class="required">*</span></label>
                <input v-model.number="returnForm.quantity" type="number" min="1" 
                       class="form-control" required :placeholder="$t('Stall.Return.Index.line179_placeholder')" />
              </div>
              <div class="form-group">
                <label>{{ $t('Stall.Return.Index.line182_text') }} <span class="required">*</span></label>
                <input v-model.number="returnForm.unitPrice" type="number" step="0.0001" 
                       class="form-control" required :placeholder="$t('Stall.Return.Index.line184_placeholder')" />
              </div>
            </div>
            
            <div class="form-group">
              <label>{{ $t('Stall.Return.Index.line189_text') }} <span class="required">*</span></label>
              <select v-model="returnForm.returnType" class="form-control" required>
                <option value="">{{ $t('common.select') }}退货类型</option>
                <option value="customer">{{ $t("return.customerReturn") }}</option>
                <option value="supplier">{{ $t("return.supplierReturn") }}</option>
                <option value="damage">{{ $t("return.damageReturn") }}</option>
              </select>
            </div>
            
            <div class="form-group">
              <label>关联{{ $t('navigation.orders') }}</label>
              <input v-model="returnForm.relatedOrderId" type="text" 
                     class="form-control" :placeholder="$t('Stall.Return.Index.line201_placeholder')" />
            </div>
            
            <div class="form-group">
              <label>{{ $t('afterSales.returnReason') }}</label>
              <textarea v-model="returnForm.reason" class="form-control" rows="3" 
                        :placeholder="$t('Stall.Return.Index.line207_placeholder')"></textarea>
            </div>
            
            <div class="form-group">
              <label>{{ $t('common.remark') }}</label>
              <textarea v-model="returnForm.remarks" class="form-control" rows="3" 
                        :placeholder="$t('Stall.Return.Index.line213_placeholder')"></textarea>
            </div>
            
            <div class="form-group total">
              <label>退货{{ $t('common.amount') }}：</label>
              <span class="total-amount">¥{{ calculateTotal() }}</span>
            </div>
            
            <div class="form-actions">
              <button type="button" class="btn btn-secondary" @click="closeReturnModal">
                {{ $t('common.cancel') }}
              </button>
              <button type="submit" class="btn btn-primary">
                {{ returnForm.id ? '更新' : '创建' }}退货单
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'ReturnManagement',
  data() {
    return {
      filters: {
        startDate: this.getDateString(-30),
        endDate: this.getDateString(0),
        productId: '',
        returnType: ''
      },
      returnList: [],
      products: [],
      showReturnModal: false,
      returnForm: {
        id: null,
        productId: '',
        quantity: 1,
        unitPrice: 0,
        returnType: '',
        relatedOrderId: '',
        reason: '',
        remarks: ''
      },
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      totalPages: 0,
      statusLabel: {
        pending: '待处理',
        processed: '已处理',
        cancelled: '已取消'
      },
      returnTypeLabel: {
        customer: '客户退货',
        supplier: '供应商退货',
        damage: '损坏退货'
      },
      totalReturnAmount: '0.00',
      totalReturnCount: 0,
      averagePrice: '0.00',
      totalItems: 0
    };
  },
  computed: {
    returnFormTitle() {
      return this.returnForm.id ? '编辑退货单' : '新增退货单';
    }
  },
  methods: {
    ...mapActions('stall', [
      'getReturns',
      'getProducts',
      'createReturn',
      'updateReturn',
      'deleteReturn',
      'getReturnDetail'
    ]),
    
    async loadReturns() {
      try {
        const response = await this.getReturns({
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
          productId: this.filters.productId,
          returnType: this.filters.returnType,
          page: this.currentPage,
          pageSize: this.pageSize
        });
        this.returnList = response.data;
        this.totalCount = response.totalCount;
        this.totalPages = Math.ceil(this.totalCount / this.pageSize);
        this.calculateStatistics();
      } catch (error) {
        this.$message.error(this.$t('return.loadFailed'));
      }
    },
    
    async loadProducts() {
      try {
        this.products = await this.getProducts();
      } catch (error) {
        console.error('加载商品失败', error);
      }
    },
    
    calculateStatistics() {
      if (this.returnList.length === 0) {
        this.totalReturnAmount = '0.00';
        this.totalReturnCount = 0;
        this.averagePrice = '0.00';
        this.totalItems = 0;
        return;
      }
      
      const total = this.returnList.reduce((sum, returnItem) => 
        sum + parseFloat(returnItem.totalAmount), 0);
      const count = this.returnList.length;
      const items = this.returnList.reduce((sum, returnItem) => 
        sum + returnItem.quantity, 0);
        
      this.totalReturnAmount = total.toFixed(2);
      this.totalReturnCount = count;
      this.averagePrice = (total / count).toFixed(2);
      this.totalItems = items;
    },
    
    searchReturns() {
      this.currentPage = 1;
      this.loadReturns();
    },
    
    resetFilters() {
      this.filters = {
        startDate: this.getDateString(-30),
        endDate: this.getDateString(0),
        productId: '',
        returnType: ''
      };
      this.currentPage = 1;
      this.loadReturns();
    },
    
    showNewReturnModal() {
      this.returnForm = {
        id: null,
        productId: '',
        quantity: 1,
        unitPrice: 0,
        returnType: '',
        relatedOrderId: '',
        reason: '',
        remarks: ''
      };
      this.showReturnModal = true;
    },
    
    closeReturnModal() {
      this.showReturnModal = false;
    },
    
    async editReturn(returnId) {
      try {
        const returnItem = await this.getReturnDetail(returnId);
        this.returnForm = {
          id: returnItem.id,
          productId: returnItem.productId,
          quantity: returnItem.quantity,
          unitPrice: returnItem.unitPrice,
          returnType: returnItem.returnType,
          relatedOrderId: returnItem.relatedOrderId,
          reason: returnItem.reason,
          remarks: returnItem.remarks
        };
        this.showReturnModal = true;
      } catch (error) {
        this.$message.error('加载退货单详情失败');
      }
    },
    
    async submitReturn() {
      try {
        if (this.returnForm.id) {
          await this.updateReturn({ id: this.returnForm.id, ...this.returnForm });
          this.$message.success('退货单已更新');
        } else {
          await this.createReturn(this.returnForm);
          this.$message.success('退货单已创建');
        }
        this.closeReturnModal();
        this.loadReturns();
      } catch (error) {
        this.$message.error('操作失败');
      }
    },
    
    async deleteReturn(returnId) {
      if (!confirm('确定要删除这条退货记录吗？')) {
        return;
      }
      
      try {
        await this.deleteReturn(returnId);
        this.$message.success('退货记录已删除');
        this.loadReturns();
      } catch (error) {
        this.$message.error('删除失败');
      }
    },
    
    viewReturnDetail(returnId) {
      this.$router.push(`/stall/return/${returnId}`);
    },
    
    calculateTotal() {
      return (this.returnForm.quantity * this.returnForm.unitPrice).toFixed(2);
    },
    
    exportReturns() {
      this.$message.success(this.$t('return.exporting'));
    },
    
    formatDate(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },
    
    getDateString(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  },
  
  mounted() {
    this.loadProducts();
    this.loadReturns();
  }
};
</script>

<style scoped lang="scss">
.return-management {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
  
  .page-header {
    margin-bottom: 20px;
    h1 {
      font-size: 24px;
      font-weight: bold;
      color: #2c3e50;
      margin: 0 0 8px 0;
    }
    .subtitle {
      color: #999;
      font-size: 13px;
      margin: 0;
    }
  }
  
  // 搜索和筛选
  .search-section {
    background: white;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    
    .search-form {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 12px;
      margin-bottom: 16px;
      
      .form-group {
        display: flex;
        flex-direction: column;
        label {
          font-size: 12px;
          font-weight: 500;
          color: #666;
          margin-bottom: 4px;
        }
        .form-control {
          padding: 8px 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 12px;
          font-family: inherit;
          &:focus {
            outline: none;
            border-color: #3498db;
            box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
          }
        }
      }
      button {
        align-self: flex-end;
      }
    }
    
    .actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      button {
        min-width: 120px;
      }
    }
  }
  
  // 统计栏
  .statistics-bar {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
    
    .stat-item {
      background: white;
      padding: 16px;
      border-radius: 8px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      
      .label {
        font-size: 12px;
        color: #999;
        margin-right: 8px;
      }
      .value {
        font-size: 16px;
        font-weight: bold;
        color: #3498db;
      }
    }
  }
  
  // 表格容器
  .table-container {
    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    
    .return-table {
      width: 100%;
      border-collapse: collapse;
      
      thead {
        background-color: #f5f7fa;
        border-bottom: 2px solid #ddd;
        th {
          padding: 12px;
          text-align: left;
          font-size: 12px;
          font-weight: 600;
          color: #666;
        }
      }
      
      tbody {
        tr {
          border-bottom: 1px solid #eee;
          transition: background-color 0.3s ease;
          
          &:hover {
            background-color: #f9f9f9;
          }
          
          td {
            padding: 12px;
            font-size: 12px;
            color: #666;
            
            &.empty-cell {
              padding: 0;
            }
            
            &.amount {
              color: #3498db;
              font-weight: 600;
            }
            
            .status {
              display: inline-block;
              padding: 4px 8px;
              border-radius: 3px;
              font-size: 11px;
              font-weight: 500;
              
              &.status-pending {
                background-color: #fff3cd;
                color: #856404;
              }
              &.status-processed {
                background-color: #d4edda;
                color: #155724;
              }
              &.status-cancelled {
                background-color: #f8d7da;
                color: #721c24;
              }
            }
            
            .product-cell {
              .product-name {
                font-weight: 500;
                color: #2c3e50;
              }
            }
          }
        }
      }
    }
    
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
      
      i {
        font-size: 48px;
        color: #ddd;
        margin-bottom: 16px;
      }
      p {
        color: #999;
        font-size: 14px;
        margin: 0;
      }
    }
  }
  
  // 表格操作按钮
  .btn-action {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 8px;
    margin-right: 4px;
    background: none;
    border: none;
    color: #3498db;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      color: #2980b9;
    }
    
    &.danger {
      color: #e74c3c;
      &:hover {
        color: #c0392b;
      }
    }
    
    i {
      font-size: 13px;
    }
  }
  
  // 分页
  .pagination-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-top: 1px solid #eee;
    background: white;
    
    .pagination-info {
      font-size: 12px;
      color: #666;
      
      .page-size-select {
        padding: 4px 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        margin: 0 6px;
        font-size: 12px;
        background: white;
      }
    }
    
    .pagination-controls {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .btn-page {
        padding: 6px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        font-size: 12px;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 4px;
        
        &:hover:not(:disabled) {
          border-color: #3498db;
          color: #3498db;
        }
        
        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
      
      .page-info {
        font-size: 12px;
        color: #666;
        min-width: 60px;
        text-align: center;
      }
    }
  }
  
  // 模态框
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    
    .modal-dialog {
      background: white;
      border-radius: 8px;
      width: 90%;
      max-width: 600px;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      
      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        border-bottom: 1px solid #eee;
        
        h2 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #2c3e50;
        }
        
        .btn-close {
          background: none;
          border: none;
          font-size: 24px;
          color: #999;
          cursor: pointer;
          
          &:hover {
            color: #2c3e50;
          }
        }
      }
      
      .modal-body {
        padding: 20px;
        
        form {
          display: flex;
          flex-direction: column;
          gap: 16px;
          
          .form-group {
            display: flex;
            flex-direction: column;
            
            label {
              font-size: 12px;
              font-weight: 500;
              color: #666;
              margin-bottom: 6px;
              
              .required {
                color: #e74c3c;
              }
            }
            
            .form-control {
              padding: 10px 12px;
              border: 1px solid #ddd;
              border-radius: 4px;
              font-size: 12px;
              font-family: inherit;
              
              &:focus {
                outline: none;
                border-color: #3498db;
                box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
              }
            }
            
            &.total {
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
              padding: 12px;
              background-color: #f5f7fa;
              border-radius: 4px;
              
              label {
                margin: 0;
              }
              
              .total-amount {
                font-size: 18px;
                font-weight: bold;
                color: #3498db;
              }
            }
          }
          
          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }
          
          .form-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
            margin-top: 20px;
            
            button {
              min-width: 100px;
            }
          }
        }
      }
    }
  }
  
  // 通用按钮样式
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &.btn-primary {
      background-color: #3498db;
      color: white;
      
      &:hover {
        background-color: #2980b9;
      }
    }
    
    &.btn-secondary {
      background-color: #ecf0f5;
      color: #666;
      
      &:hover {
        background-color: #d5dce3;
      }
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>